/**
 * Product Analytics Events
 *
 * Typed, privacy-safe product analytics events for normal PostHog capture.
 *
 * Primary responsibilities:
 * - Define normal product event names and property contracts
 * - Sanitize product analytics properties before PostHog capture
 * - Provide a client helper for typed product events
 *
 * Dependencies:
 * - lib/analytics/track
 * - lib/billing/losi-pricing
 */

import {
  getEffectiveFeaturePlanId,
  type FeaturePlanProfileLike,
  type LosiPublicPlanId,
  type LosiSpaceObjectType
} from '@/lib/billing/losi-pricing'
import { track } from '@/lib/analytics/track'

export const ProductAnalyticsEventNames = {
  PAGE_VIEWED: 'page_viewed',
  NAV_ITEM_CLICKED: 'nav_item_clicked',
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  IMAGE_GENERATION_STARTED: 'image_generation_started',
  IMAGE_GENERATION_COMPLETED: 'image_generation_completed',
  IMAGE_GENERATION_FAILED: 'image_generation_failed',
  FILE_UPLOADED: 'file_uploaded',
  FILE_UPLOAD_FAILED: 'file_upload_failed',
  SPACE_OPENED: 'space_opened',
  SPACE_ITEM_CREATED: 'space_item_created',
  SPACE_ITEM_CREATE_ATTEMPTED: 'space_item_create_attempted',
  SPACE_OBJECT_GATE_VIEWED: 'space_object_gate_viewed',
  SPACE_OBJECT_GATE_CLICKED: 'space_object_gate_clicked',
  SPACE_OBJECT_GATE_DISMISSED: 'space_object_gate_dismissed',
  WORKSPACE_EFFECTIVE_PLAN_RESOLVED: 'workspace_effective_plan_resolved',
  FREE_LIMIT_APPROACHED: 'free_limit_approached',
  FREE_LIMIT_REACHED: 'free_limit_reached',
  UPGRADE_LIMIT_REACHED: 'upgrade_limit_reached',
  PRICING_PAGE_VIEWED: 'pricing_page_viewed',
  UPGRADE_PROMPT_VIEWED: 'upgrade_prompt_viewed',
  UPGRADE_PROMPT_CLICKED: 'upgrade_prompt_clicked',
  UPGRADE_PROMPT_DISMISSED: 'upgrade_prompt_dismissed',
  UPGRADE_FALLBACK_USED: 'upgrade_fallback_used',
  LOCKED_FEATURE_CLICKED: 'locked_feature_clicked',
  PLAN_TO_TASK_CONVERSION_ATTEMPTED: 'plan_to_task_conversion_attempted',
  PLAN_CHECKOUT_STARTED: 'plan_checkout_started',
  PLAN_UPGRADE_COMPLETED: 'plan_upgrade_completed',
  // Churn prevention / cancel flow (see churn-prevention skill)
  BILLING_PORTAL_OPENED: 'billing_portal_opened',
  CANCEL_FLOW_STARTED: 'cancel_flow_started',
  CANCEL_SURVEY_SUBMITTED: 'cancel_survey_submitted',
  SAVE_OFFER_SHOWN: 'save_offer_shown',
  SAVE_OFFER_ACCEPTED: 'save_offer_accepted',
  SAVE_OFFER_DECLINED: 'save_offer_declined',
  SUBSCRIPTION_CANCELED: 'subscription_canceled',
  PAYMENT_FAILED: 'payment_failed',
  WALLET_OVERAGE_VIEWED: 'wallet_overage_viewed',
  WALLET_LIMIT_REACHED: 'wallet_limit_reached',
  WALLET_TOPUP_VIEWED: 'wallet_topup_viewed',
  WALLET_TOPUP_STARTED: 'wallet_topup_started',
  WALLET_TOPUP_COMPLETED: 'wallet_topup_completed',
  WALLET_AUTO_LOAD_ENABLED: 'wallet_auto_load_enabled',
  WALLET_AUTO_LOAD_DISABLED: 'wallet_auto_load_disabled',
  ATTACHMENT_LIMIT_VIEWED: 'attachment_limit_viewed',
  ATTACHMENT_LIMIT_HIT: 'attachment_limit_hit',
  CONTEXT_LIMIT_HIT: 'context_limit_hit',
  USAGE_LIMIT_HIT: 'usage_limit_hit',
  PRIORITY_SPEED_UPSELL_VIEWED: 'priority_speed_upsell_viewed',
  NEXUS_HANDOFF_VIEWED: 'nexus_handoff_viewed',
  NEXUS_HANDOFF_CLICKED: 'nexus_handoff_clicked',
  NEXUS_HANDOFF_DISMISSED: 'nexus_handoff_dismissed',
  // Discoverability / Losi-max journey (first 14–30 days)
  FEATURE_USED: 'feature_used',
  FEATURE_DISCOVERY_PROMPT_SHOWN: 'feature_discovery_prompt_shown',
  FEATURE_DISCOVERY_PROMPT_CLICKED: 'feature_discovery_prompt_clicked',
  FEATURE_DISCOVERY_PROMPT_DISMISSED: 'feature_discovery_prompt_dismissed',
  FEATURE_DISCOVERY_TOOLTIP_SHOWN: 'feature_discovery_tooltip_shown',
  FEATURE_DISCOVERY_TOOLTIP_CLICKED: 'feature_discovery_tooltip_clicked',
  FEATURE_DISCOVERY_TOOLTIP_DISMISSED: 'feature_discovery_tooltip_dismissed',
  FEATURE_ONBOARDING_COMPLETED: 'feature_onboarding_completed'
} as const

export type ProductAnalyticsEventName =
  (typeof ProductAnalyticsEventNames)[keyof typeof ProductAnalyticsEventNames]

export type ProductAnalyticsSurface =
  | 'chat'
  | 'space'
  | 'spaces'
  | 'note'
  | 'task'
  | 'event'
  | 'sprint'
  | 'pricing'
  | 'settings'
  | 'wallet'
  | 'nexus'
  | 'navigation'
  | 'files'
  | 'billing'
  | 'homepage'
  | 'public_chat'
  | 'sidekick'
  | 'embed'
  | 'other'
  | 'unknown'

export type ProductAnalyticsTrigger =
  | 'locked_feature_click'
  | 'locked_feature_clicked'
  | 'limit_approached'
  | 'limit_reached'
  | 'limit_hit'
  | 'soft_preview'
  | 'pricing_page'
  | 'settings'
  | 'business_intent'
  | 'manual_click'
  | 'route_change'
  | 'checkout'
  | 'manage'
  | 'cancel'
  | 'payment_method_update'
  | 'dismiss'
  | 'plan_created'
  | 'action_items_detected'
  | 'empty_state'
  | 'repeated_project_behavior'
  | 'user_intent_detected'
  | 'other'

export type ProductAnalyticsPromptType = 'education_preview' | 'hard_gate'

export type ProductAnalyticsFreePlusVariant =
  | 'scale_education'
  | 'limit_only'

export type ProductAnalyticsLimitType =
  | 'task_count'
  | 'plan_to_tasks_count'
  | 'recurring_tasks'
  | 'events'
  | 'sprints'
  | 'file'
  | 'other'

export type ProductAnalyticsUpgradeCta =
  | 'upgrade_to_plus'
  | 'keep_using_free'
  | 'not_now'
  | 'learn_more'

export type ProductAnalyticsDismissMethod =
  | 'secondary_cta'
  | 'close'
  | 'outside_click'
  | 'escape'
  | 'other'

export type ProductAnalyticsFallback =
  | 'save_as_note'
  | 'keep_in_chat'
  | 'create_folder'
  | 'create_without_recurrence'
  | 'copy_to_clipboard'
  | 'other'

export type ProductAnalyticsLockedFeature =
  | 'task'
  | 'event'
  | 'sprint'
  | 'chat_to_tasks'
  | 'note_to_tasks'
  | 'schedule_event'
  | 'sprint_cycle'

export type ImageGenerationAnalyticsProperties = ProductAnalyticsBaseProperties & {
  surface: 'chat'
  operation: 'generate' | 'edit'
  /** Number of reference images actually sent to the provider. 0 = text-to-image. */
  image_count: number
  /** Reference URLs dropped as dead/unresolvable before the provider call. */
  dropped_image_count?: number
  provider?: string
  model?: string
  model_tier_requested?: string
  model_tier_effective?: string
  tier_downgrade_reason?: string
  tier_auto_upgrade_reason?: string
  input_fidelity?: string
  aspect_ratio?: string
  used_fallback?: boolean
  duration_ms?: number
  error_code?: string
  chat_id?: string
  tool?: string
}

type ProductAnalyticsPrimitive = string | number | boolean | null | undefined

type ProductAnalyticsBaseProperties = {
  surface: ProductAnalyticsSurface
  source?: string
  from_plan?: LosiPublicPlanId
  to_plan?: LosiPublicPlanId
  current_plan?: LosiPublicPlanId
  effective_plan?: LosiPublicPlanId
  workspace_plan?: LosiPublicPlanId
  recommended_plan?: LosiPublicPlanId
  required_plan?: LosiPublicPlanId
  tier_suggested?: LosiPublicPlanId
  trigger?: ProductAnalyticsTrigger
  experiment_key?: string
  feature_flag?: string
  variant?: string
  session_id?: string
  workspace_id?: string
}

type SpaceItemAnalyticsProperties = ProductAnalyticsBaseProperties & {
  surface: 'spaces'
  item_type: LosiSpaceObjectType
  locked_object_type?: LosiSpaceObjectType
  required_plan?: LosiPublicPlanId
  space_id?: string
}

type UpgradeAnalyticsProperties = ProductAnalyticsBaseProperties & {
  surface: ProductAnalyticsSurface
  recommended_plan?: LosiPublicPlanId
  required_plan?: LosiPublicPlanId
  prompt_id?: string
  prompt_type?: ProductAnalyticsPromptType
  limit_type?: ProductAnalyticsLimitType
  current_usage?: number
  limit?: number
  limit_value?: number
  locked_feature?: ProductAnalyticsLockedFeature
  object_type?: LosiSpaceObjectType | 'file'
  item_type?: LosiSpaceObjectType
  item_id?: string
  space_id?: string
  cta?: ProductAnalyticsUpgradeCta
  dismiss_method?: ProductAnalyticsDismissMethod
  dismiss_reason?: string
  dismiss_count?: number
  fallback?: ProductAnalyticsFallback
  fallback_offered?: ProductAnalyticsFallback
}

export type ProductAnalyticsEventPropertiesByName = {
  [ProductAnalyticsEventNames.PAGE_VIEWED]: ProductAnalyticsBaseProperties & {
    page_path?: string
    page_title?: string
  }
  [ProductAnalyticsEventNames.NAV_ITEM_CLICKED]: ProductAnalyticsBaseProperties & {
    target: string
    destination_path?: string
  }
  [ProductAnalyticsEventNames.CHAT_MESSAGE_SENT]: ProductAnalyticsBaseProperties & {
    message_length: number
    is_first_message?: boolean
  }
  [ProductAnalyticsEventNames.IMAGE_GENERATION_STARTED]: ImageGenerationAnalyticsProperties
  [ProductAnalyticsEventNames.IMAGE_GENERATION_COMPLETED]: ImageGenerationAnalyticsProperties & {
    duration_ms: number
  }
  [ProductAnalyticsEventNames.IMAGE_GENERATION_FAILED]: ImageGenerationAnalyticsProperties & {
    duration_ms: number
    error_code: string
  }
  [ProductAnalyticsEventNames.FILE_UPLOADED]: ProductAnalyticsBaseProperties & {
    file_count: number
    file_size_bytes?: number
  }
  [ProductAnalyticsEventNames.FILE_UPLOAD_FAILED]: ProductAnalyticsBaseProperties & {
    file_count: number
    file_size_bytes?: number
    reason: string
    file_extension?: string
  }
  [ProductAnalyticsEventNames.SPACE_OPENED]: ProductAnalyticsBaseProperties & {
    surface: 'spaces'
    space_id?: string
    source?: string
  }
  [ProductAnalyticsEventNames.SPACE_ITEM_CREATED]: SpaceItemAnalyticsProperties
  [ProductAnalyticsEventNames.SPACE_ITEM_CREATE_ATTEMPTED]: SpaceItemAnalyticsProperties
  [ProductAnalyticsEventNames.SPACE_OBJECT_GATE_VIEWED]: SpaceItemAnalyticsProperties
  [ProductAnalyticsEventNames.SPACE_OBJECT_GATE_CLICKED]: SpaceItemAnalyticsProperties
  [ProductAnalyticsEventNames.SPACE_OBJECT_GATE_DISMISSED]:
    SpaceItemAnalyticsProperties & {
      dismiss_reason?: string
    }
  [ProductAnalyticsEventNames.WORKSPACE_EFFECTIVE_PLAN_RESOLVED]:
    ProductAnalyticsBaseProperties & {
      current_plan: LosiPublicPlanId
      effective_plan: LosiPublicPlanId
      workspace_plan?: LosiPublicPlanId
      workspace_id?: string
      feature_flag: string
      flag_enabled: boolean
      inheritance_applied: boolean
    }
  [ProductAnalyticsEventNames.FREE_LIMIT_APPROACHED]:
    ProductAnalyticsBaseProperties & {
      from_plan: 'free'
      to_plan: 'plus'
      limit_type: ProductAnalyticsLimitType
      current_usage: number
      limit: number
      limit_value?: number
    }
  [ProductAnalyticsEventNames.FREE_LIMIT_REACHED]:
    ProductAnalyticsBaseProperties & {
      from_plan: 'free'
      to_plan: 'plus'
      limit_type: ProductAnalyticsLimitType
      current_usage: number
      limit: number
      limit_value?: number
    }
  [ProductAnalyticsEventNames.UPGRADE_LIMIT_REACHED]:
    ProductAnalyticsBaseProperties & {
      current_plan: LosiPublicPlanId
      effective_plan?: LosiPublicPlanId
      workspace_plan?: LosiPublicPlanId
      limit_type: ProductAnalyticsLimitType
      current_usage: number
      limit_value: number
      object_type?: LosiSpaceObjectType | 'file'
      tier_suggested: LosiPublicPlanId
      fallback_offered?: ProductAnalyticsFallback
      workspace_id?: string
    }
  [ProductAnalyticsEventNames.PRICING_PAGE_VIEWED]: ProductAnalyticsBaseProperties & {
    surface: 'pricing'
  }
  [ProductAnalyticsEventNames.UPGRADE_PROMPT_VIEWED]: UpgradeAnalyticsProperties
  [ProductAnalyticsEventNames.UPGRADE_PROMPT_CLICKED]: UpgradeAnalyticsProperties
  [ProductAnalyticsEventNames.UPGRADE_PROMPT_DISMISSED]: UpgradeAnalyticsProperties
  [ProductAnalyticsEventNames.UPGRADE_FALLBACK_USED]: UpgradeAnalyticsProperties
  [ProductAnalyticsEventNames.LOCKED_FEATURE_CLICKED]:
    ProductAnalyticsBaseProperties & {
      current_plan: LosiPublicPlanId
      required_plan: LosiPublicPlanId
      feature: ProductAnalyticsLockedFeature
      space_id?: string
      item_type?: LosiSpaceObjectType
      item_id?: string
    }
  [ProductAnalyticsEventNames.PLAN_TO_TASK_CONVERSION_ATTEMPTED]:
    ProductAnalyticsBaseProperties & {
      current_plan: LosiPublicPlanId
      effective_plan?: LosiPublicPlanId
      workspace_plan?: LosiPublicPlanId
      requested_task_count: number
      created_task_count: number
      remaining_conversion_allowance: number
      block_reason?: string
      fallback_offered?: ProductAnalyticsFallback
      limit_type: ProductAnalyticsLimitType
      limit_value: number
      workspace_id?: string
    }
  [ProductAnalyticsEventNames.PLAN_CHECKOUT_STARTED]: UpgradeAnalyticsProperties & {
    selected_plan?: string
    billing_cycle?: string
    billing_period?: string
    amount?: number
    currency?: string
    source_event?: string
  }
  [ProductAnalyticsEventNames.PLAN_UPGRADE_COMPLETED]: UpgradeAnalyticsProperties & {
    selected_plan?: string
    billing_cycle?: string
    billing_period?: string
    amount?: number
    currency?: string
    source_event?: string
  }
  [ProductAnalyticsEventNames.BILLING_PORTAL_OPENED]: ProductAnalyticsBaseProperties & {
    surface: 'billing' | 'settings' | 'pricing'
  }
  [ProductAnalyticsEventNames.CANCEL_FLOW_STARTED]: ProductAnalyticsBaseProperties & {
    surface: 'billing' | 'settings'
    source_event?: string
  }
  [ProductAnalyticsEventNames.CANCEL_SURVEY_SUBMITTED]: ProductAnalyticsBaseProperties & {
    surface: 'billing' | 'settings'
    reason: string
  }
  [ProductAnalyticsEventNames.SAVE_OFFER_SHOWN]: ProductAnalyticsBaseProperties & {
    surface: 'billing' | 'settings'
    reason?: string
    offer_type: string
  }
  [ProductAnalyticsEventNames.SAVE_OFFER_ACCEPTED]: ProductAnalyticsBaseProperties & {
    surface: 'billing' | 'settings'
    reason?: string
    offer_type: string
  }
  [ProductAnalyticsEventNames.SAVE_OFFER_DECLINED]: ProductAnalyticsBaseProperties & {
    surface: 'billing' | 'settings'
    reason?: string
    offer_type: string
  }
  [ProductAnalyticsEventNames.SUBSCRIPTION_CANCELED]: ProductAnalyticsBaseProperties & {
    surface: 'billing' | 'settings' | 'pricing'
    reason?: string
    source_event?: string
  }
  [ProductAnalyticsEventNames.PAYMENT_FAILED]: ProductAnalyticsBaseProperties & {
    surface: 'billing'
    reason?: string
    amount?: number
    currency?: string
    source_event?: string
  }
  [ProductAnalyticsEventNames.WALLET_OVERAGE_VIEWED]: ProductAnalyticsBaseProperties & {
    surface: 'wallet' | 'billing' | 'nexus'
    wallet_balance_cents?: number
    credits_used?: number
    credits_limit?: number
  }
  [ProductAnalyticsEventNames.WALLET_LIMIT_REACHED]: ProductAnalyticsBaseProperties & {
    surface: 'wallet' | 'billing' | 'nexus'
    wallet_balance_cents?: number
    credits_used?: number
    credits_limit?: number
  }
  [ProductAnalyticsEventNames.WALLET_TOPUP_VIEWED]: ProductAnalyticsBaseProperties & {
    surface: 'wallet' | 'billing' | 'nexus'
    amount_cents?: number
    wallet_balance_cents?: number
  }
  [ProductAnalyticsEventNames.WALLET_TOPUP_STARTED]: ProductAnalyticsBaseProperties & {
    surface: 'wallet' | 'billing' | 'nexus'
    amount_cents?: number
    wallet_balance_cents?: number
  }
  [ProductAnalyticsEventNames.WALLET_TOPUP_COMPLETED]: ProductAnalyticsBaseProperties & {
    surface: 'wallet' | 'billing' | 'nexus'
    amount_cents?: number
    wallet_balance_cents?: number
  }
  [ProductAnalyticsEventNames.WALLET_AUTO_LOAD_ENABLED]: ProductAnalyticsBaseProperties & {
    surface: 'wallet' | 'billing' | 'nexus'
  }
  [ProductAnalyticsEventNames.WALLET_AUTO_LOAD_DISABLED]: ProductAnalyticsBaseProperties & {
    surface: 'wallet' | 'billing' | 'nexus'
  }
  [ProductAnalyticsEventNames.ATTACHMENT_LIMIT_VIEWED]: ProductAnalyticsBaseProperties & {
    max_files?: number
    max_attachment_size_mb?: number
  }
  [ProductAnalyticsEventNames.ATTACHMENT_LIMIT_HIT]: ProductAnalyticsBaseProperties & {
    file_count?: number
    file_size_bytes?: number
    max_files?: number
    max_attachment_size_mb?: number
  }
  [ProductAnalyticsEventNames.CONTEXT_LIMIT_HIT]: ProductAnalyticsBaseProperties & {
    limit?: number
  }
  [ProductAnalyticsEventNames.USAGE_LIMIT_HIT]: ProductAnalyticsBaseProperties & {
    limit?: number
  }
  [ProductAnalyticsEventNames.PRIORITY_SPEED_UPSELL_VIEWED]: ProductAnalyticsBaseProperties
  [ProductAnalyticsEventNames.NEXUS_HANDOFF_VIEWED]: ProductAnalyticsBaseProperties & {
    surface: 'nexus' | 'chat' | 'spaces'
    trigger: 'business_intent' | 'soft_preview' | 'manual_click'
  }
  [ProductAnalyticsEventNames.NEXUS_HANDOFF_CLICKED]: ProductAnalyticsBaseProperties & {
    surface: 'nexus' | 'chat' | 'spaces'
    trigger: 'business_intent' | 'soft_preview' | 'manual_click'
  }
  [ProductAnalyticsEventNames.NEXUS_HANDOFF_DISMISSED]: ProductAnalyticsBaseProperties & {
    surface: 'nexus' | 'chat' | 'spaces'
    trigger: 'business_intent' | 'soft_preview' | 'manual_click'
    dismiss_reason?: string
  }
  [ProductAnalyticsEventNames.FEATURE_USED]: ProductAnalyticsBaseProperties & {
    feature_key: string
    trigger?: string
  }
  [ProductAnalyticsEventNames.FEATURE_DISCOVERY_PROMPT_SHOWN]: ProductAnalyticsBaseProperties & {
    feature_key: string
    trigger?: string
  }
  [ProductAnalyticsEventNames.FEATURE_DISCOVERY_PROMPT_CLICKED]: ProductAnalyticsBaseProperties & {
    feature_key: string
    trigger?: string
  }
  [ProductAnalyticsEventNames.FEATURE_DISCOVERY_PROMPT_DISMISSED]: ProductAnalyticsBaseProperties & {
    feature_key: string
    trigger?: string
  }
  [ProductAnalyticsEventNames.FEATURE_DISCOVERY_TOOLTIP_SHOWN]: ProductAnalyticsBaseProperties & {
    feature_key: string
    trigger?: string
  }
  [ProductAnalyticsEventNames.FEATURE_DISCOVERY_TOOLTIP_CLICKED]: ProductAnalyticsBaseProperties & {
    feature_key: string
    trigger?: string
  }
  [ProductAnalyticsEventNames.FEATURE_DISCOVERY_TOOLTIP_DISMISSED]: ProductAnalyticsBaseProperties & {
    feature_key: string
    trigger?: string
  }
  [ProductAnalyticsEventNames.FEATURE_ONBOARDING_COMPLETED]: ProductAnalyticsBaseProperties & {
    feature_key: string
    trigger?: string
  }
}

export const PRODUCT_ANALYTICS_EVENT_NAME_SET = new Set<string>(
  Object.values(ProductAnalyticsEventNames)
)

const LEGACY_PRODUCT_EVENT_NAMES = [
  'user_signup',
  'user_login',
  'user_logout',
  'signup',
  'login',
  'logout',
  'feature_flag_impression',
  'feature_flag_conversion',
  'button_click',
  'link_click',
  'page_view',
  'settings_updated',
  'model_selected',
  'theme_changed',
  'tool_selected',
  'tool_executed',
  'tool_error',
  'payment_completed',
  'subscription_started',
  'subscription_canceled',
  'subscription_upgraded',
  'subscription_downgraded',
  'password_reset_request',
  'error_occurred',
  'public_chat_starter_selected',
  'public_chat_save_cta_clicked',
  'public_chat_save_cta_viewed',
  'nexus_operator_journey_event',
  'nexus_landing_viewed',
  'nexus_demo_started',
  'nexus_demo_completed',
  'nexus_signup_started',
  'nexus_signup_completed',
  'nexus_onboarding_started',
  'nexus_onboarding_step_completed',
  'nexus_onboarding_completed',
  'nexus_pricing_viewed',
  'nexus_plan_selected',
  'nexus_checkout_started',
  'nexus_checkout_completed',
  'nexus_trial_started',
  'nexus_trial_expiring',
  'nexus_trial_converted',
  'nexus_trial_expired',
  'nexus_payment_modal_shown',
  'nexus_payment_modal_cta_clicked',
  'nexus_payment_modal_dismissed',
  // Growth marketing system (see docs/GROWTH_MARKETING_SYSTEM.md)
  'signup_started',
  'growth_campaign_landing_viewed',
  'growth_cta_clicked',
  'referral_link_copied',
  'referral_invite_sent',
  'referral_signup_attributed',
  'share_nudge_shown',
  'share_nudge_clicked',
  'share_nudge_dismissed',
  'activation_checklist_shown',
  'activation_checklist_item_completed',
  'activation_checklist_completed',
  'lifecycle_email_cta_clicked'
] as const

export const PRODUCT_ANALYTICS_CAPTURE_EVENT_NAME_SET = new Set<string>([
  ...PRODUCT_ANALYTICS_EVENT_NAME_SET,
  ...LEGACY_PRODUCT_EVENT_NAMES
])

const SAFE_PRODUCT_PROPERTY_KEYS = new Set([
  'action',
  'amount',
  'aspect_ratio',
  'chat_id',
  'dropped_image_count',
  'duration_ms',
  'error_code',
  'image_count',
  'input_fidelity',
  'model',
  'model_tier_effective',
  'model_tier_requested',
  'operation',
  'tier_auto_upgrade_reason',
  'tier_downgrade_reason',
  'provider',
  'tool',
  'used_fallback',
  'amount_cents',
  'billing_period',
  'billing_cycle',
  'block_reason',
  'button',
  'cta',
  'credits_limit',
  'credits_remaining',
  'credits_used',
  'currency',
  'current_usage',
  'current_plan',
  'created_task_count',
  'destination',
  'destination_path',
  'dismiss_method',
  'dismiss_count',
  'dismiss_reason',
  'effective_plan',
  'experiment_key',
  'fallback',
  'fallback_offered',
  'feature',
  'feature_flag',
  'feature_key',
  'file_count',
  'file_extension',
  'file_size_bytes',
  'first_run',
  'flag_enabled',
  'from_plan',
  'funnel',
  'has_attachments',
  'inheritance_applied',
  'is_anonymous',
  'is_first_message',
  'is_trial',
  'item_id',
  'item_type',
  'label',
  'limit',
  'limit_value',
  'limit_type',
  'locked_feature',
  'locked_object_type',
  'max_attachment_size_mb',
  'max_files',
  'message_count',
  'message_length',
  'method',
  'object_type',
  'offer_type',
  'page_path',
  'page_title',
  'plan',
  'prompt_id',
  'prompt_type',
  'reason',
  'recommended_plan',
  'remaining_conversion_allowance',
  'required_plan',
  'requested_task_count',
  'selected_plan',
  'source',
  'source_event',
  'space_id',
  'surface',
  'target',
  'tier_suggested',
  'to_plan',
  'trigger',
  'variant',
  'wallet_balance_cents',
  'workspace_plan',
  'workspace_id',
  // Growth attribution / loops
  'campaign',
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'referrer_code',
  'checklist_item',
  'email_sequence',
  'redirect_to'
])

const STRING_PROPERTY_MAX_LENGTH = 180

function normalizePropertyKey(key: string) {
  if (key === 'sessionId') return 'session_id'
  if (key === 'workspaceId') return 'workspace_id'
  if (key === 'spaceId') return 'space_id'
  return key
}

function sanitizePrimitive(value: unknown): ProductAnalyticsPrimitive {
  if (value === null) return null

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return undefined
    return trimmed.slice(0, STRING_PROPERTY_MAX_LENGTH)
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }

  if (typeof value === 'boolean') {
    return value
  }

  return undefined
}

function readSafeString(value: unknown) {
  const sanitized = sanitizePrimitive(value)
  return typeof sanitized === 'string' ? sanitized : null
}

export function getAnalyticsPlanId(
  profile: FeaturePlanProfileLike | null | undefined
): LosiPublicPlanId {
  return getEffectiveFeaturePlanId(profile)
}

export function sanitizeProductAnalyticsProperties(
  eventName: string,
  properties: Record<string, unknown>,
  userId: string | null
): Record<string, ProductAnalyticsPrimitive> | null {
  if (!PRODUCT_ANALYTICS_CAPTURE_EVENT_NAME_SET.has(eventName)) {
    return null
  }

  const sanitized: Record<string, ProductAnalyticsPrimitive> = {
    analytics_schema: 'product_v1'
  }

  for (const [rawKey, rawValue] of Object.entries(properties)) {
    const key = normalizePropertyKey(rawKey)

    if (key === 'session_id') {
      const sessionId = readSafeString(rawValue)
      if (sessionId) sanitized.session_id = sessionId
      continue
    }

    if (!SAFE_PRODUCT_PROPERTY_KEYS.has(key)) {
      continue
    }

    const value = sanitizePrimitive(rawValue)
    if (value !== undefined) {
      sanitized[key] = value
    }
  }

  if (userId) {
    sanitized.user_id = userId
  }

  return sanitized
}

export function trackProductEvent<Name extends ProductAnalyticsEventName>(
  eventName: Name,
  properties: ProductAnalyticsEventPropertiesByName[Name]
) {
  return track(eventName, properties)
}
